import { buildIntegrationPrompt } from '@admin-platform/shared/integration-content';
import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow, Split } from '../components/Diagram';
const STEPS: { title: string; body: string; entity?: string; entityLabel?: string; figure?: ReactNode }[] = [
  {
    title: 'Why this exists',
    body: 'Revvly already invoices on its own schedule — what it cannot see is your bank account or your payment processor\'s ledger. The invoice payment webhook closes that gap: the moment your PSP, bank feed, or accounting sync confirms money moved, you POST it to https://api.revvly.ai/api/v1/webhooks/invoice-payment and the invoice settles itself. No one has to open the dashboard and click Mark Paid.',
    entity: 'invoice',
    entityLabel: 'Invoice',
  },
  {
    title: 'Get a scoped API key',
    body: 'Issue or reuse an API key under Settings → API Keys and tick Can record payments (the PAYMENT_WRITE capability); without it the webhook answers 403 "API key lacks required capability: PAYMENT_WRITE". Keys that were live on 15 October 2026 already hold it. Two secrets come back the moment you create or rotate one — a Bearer token and a signing secret — both shown once. If your key predates signing secrets, rotate it to provision one; a Bearer token with no signing secret can authenticate but can never pass the signature check. If the key is endpoint-scoped, include /webhooks/invoice-payment (or the trailing-wildcard /webhooks/*) in that scope.',
    entity: 'apiKey',
    entityLabel: 'API Key',
  },
  {
    title: 'Sign the raw body',
    body: 'Compute an HMAC-SHA256 over the exact request bytes, keyed with the signing secret, and send it as X-Signature-SHA256: sha256=<hex>. Sign after you have finished building the JSON — re-serializing it afterwards, even just reordering keys or whitespace, produces different bytes and invalidates the signature. It is the same mechanism the usage-ingestion endpoint uses.',
  },
  {
    title: 'Point at the right invoice',
    body: 'Identify the invoice with invoiceNumber — the printed number, e.g. ACME-00042, usually the same reference your processor or bank feed already carries — or invoiceId if you already sync Revvly\'s internal ids. At least one is required; if you send both, invoiceId wins.',
    entity: 'invoice',
    entityLabel: 'Invoice',
  },
  {
    title: 'Full settlement or a part payment',
    body: 'Omit amount and Revvly settles whatever is currently outstanding — the easy path when you know the payment clears the balance. Include amount to record a part payment; it is added to amountPaid and the invoice keeps whatever status it already had — Pending, Overdue, or Failed — still visible in AR and still drawing reminders, until a later call brings the balance to zero. Only an already-Paid or Cancelled invoice refuses a payment outright.',
  },
  {
    title: 'Make retries free',
    body: 'Idempotency-Key is required on every call — use your processor\'s own event id. A key already recorded on the invoice is never applied a second time: the response comes back 200 with replayed: true and the invoice\'s current totals, so your sender sees success and stops retrying instead of double-counting a payment.',
  },
  {
    title: 'Read the response, then move on',
    body: 'The 200 body is the invoice as it now stands — amountPaid, amountDue, fullyPaid, and appliedAmount (what this specific call moved) — enough to reconcile without a follow-up read. A 409 means the invoice cannot take a payment at all: already paid, cancelled, or nothing left owing. Treat it as a stop, not something to retry later.',
    figure: (
      <Figure
        title="What happens to a payment call"
        caption="A repeat is acknowledged without applying anything, and a settled invoice is refused."
      >
        <Flow
          steps={[
            { title: 'Signed POST', text: 'With an Idempotency-Key and the invoice number.' },
            {
              fork: [
                {
                  title: 'Amount omitted',
                  text: 'Settles whatever is outstanding. The invoice becomes Paid.',
                  tone: 'accent',
                },
                {
                  title: 'Amount given',
                  text: 'Added to amountPaid. The invoice keeps its status until the balance is zero.',
                  tone: 'accent',
                },
                {
                  title: 'Key already recorded',
                  text: '200 with replayed: true. Nothing is applied again.',
                  tone: 'muted',
                },
                {
                  title: 'Already paid or cancelled',
                  text: '409. Stop, do not retry.',
                  tone: 'danger',
                },
              ],
            },
          ]}
        />
      </Figure>
    ),
  },
];

const WORKED_STEPS: { label: string; text: string }[] = [
  {
    label: 'The invoice',
    text: "Acme Corp's April cycle produces invoice ACME-00042 for $1,000, due on the 23rd. It sits Pending in Revvly, like every other renewal invoice.",
  },
  {
    label: 'The charge lands',
    text: 'Acme pays $400 by card through your checkout. Your payment processor fires a charge.succeeded webhook carrying event id evt_3PqR8s2eZvKYlo2C.',
  },
  {
    label: 'You forward it',
    text: "Your handler signs { invoiceNumber: 'ACME-00042', amount: 400, currency: 'USD', reference: 'pi_3PqR8s2eZvKYlo2C' } with your signing secret and POSTs it to https://api.revvly.ai/api/v1/webhooks/invoice-payment with Idempotency-Key: evt_3PqR8s2eZvKYlo2C.",
  },
  {
    label: 'Revvly applies it',
    text: '200 OK comes back with amountPaid: 400, amountDue: 600, fullyPaid: false. The invoice stays Pending — now correctly showing $600 still owed.',
  },
  {
    label: 'The balance clears',
    text: 'Three weeks later Acme pays the remaining $600. A second call — new Idempotency-Key, amount omitted this time — settles it: amountPaid: 1000, amountDue: 0, fullyPaid: true, and the invoice flips to Paid.',
  },
  {
    label: 'A retry costs nothing',
    text: 'Your processor redelivers the first webhook a minute after you already processed it. Same Idempotency-Key, same body — Revvly returns replayed: true and applies nothing a second time.',
  },
];

// Shared with the dashboard's copy-prompt button so an operator and an agent
// read the same contract. Edit the guide in libs/shared/integration-content.
const AGENT_PROMPT = buildIntegrationPrompt('invoicePayments');

export function WebhookPaymentGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Webhook Payment Integration — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Webhook payment integration</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Wire up the payment <em>webhook</em>.
        </h1>
        <p className="dp-oneliner">
          Post a payment to Revvly the instant your processor or bank feed sees it land —
          in full, or a part payment that leaves the rest outstanding. Invoices settle
          themselves; nobody has to open the dashboard.
        </p>
      </header>

      {STEPS.map((step, i) => (
        <div key={i} className="dp-section">
          <h2>
            <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            {step.title}
          </h2>
          <p className="dp-plain">{step.body}</p>
          {step.figure}
          {step.entity && (
            <ul className="dp-connects" style={{ marginTop: 14 }}>
              <li>
                <Link to={`/docs/concepts/${step.entity}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                  {step.entityLabel} concept ›
                </Link>
              </li>
            </ul>
          )}
        </div>
      ))}

      <div className="we-wrap" style={{ padding: '48px 0 24px' }}>
        <div className="we-header">
          <span className="rv-eyebrow-mini">A worked example</span>
          <h3>
            Acme Corp pays in <em>two parts</em>.
          </h3>
          <p>
            One invoice, one partial payment, one full settlement, and a redelivered
            webhook that changes nothing.
          </p>
        </div>
        <ol className="we-steps">
          {WORKED_STEPS.map((s, i) => (
            <li key={i}>
              <div className="we-step-num">{String(i + 1).padStart(2, '0')}</div>
              <div>
                <div className="we-step-label">{s.label}</div>
                <div className="we-step-text">{s.text}</div>
              </div>
            </li>
          ))}
        </ol>
        <Figure
          title="How the $1,000 invoice was paid"
          caption="Two payments add up to the invoice total, and the redelivered webhook adds nothing."
        >
          <Split
            parts={[
              { label: 'First payment', value: 400, amount: '$400', tone: 'accent' },
              { label: 'Second payment', value: 600, amount: '$600' },
            ]}
            total="$1,000"
          />
        </Figure>
      </div>

      <section className="dp-section">
        <h2>Give this prompt to an integration agent</h2>
        <p className="dp-plain">
          Use this context when asking an agent to build a payment-processor forwarding handler.
        </p>
        <pre className="api-code">{AGENT_PROMPT}</pre>
      </section>

      <div className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/api/invoice-payments" style={{ color: 'inherit', textDecoration: 'none' }}>
              Invoice Payment Webhook — full API reference ›
            </Link>
          </li>
          <li>
            <Link to="/docs/api/events" style={{ color: 'inherit', textDecoration: 'none' }}>
              Event Ingestion API ›
            </Link>
          </li>
          <li>
            <Link to="/docs/concepts/invoice" style={{ color: 'inherit', textDecoration: 'none' }}>
              The Invoice concept ›
            </Link>
          </li>
        </ul>
      </div>
    </div>
  );
}
